import React from 'react';
import { useThemeColors } from '../hooks/useThemeColors';

const ThemeColorDemo: React.FC = () => {
  const { primary, secondary, primaryShades, secondaryShades } = useThemeColors();

  const ColorSwatch: React.FC<{ color: string; name: string; shade?: string }> = ({ color, name, shade }) => (
    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm border">
      <div 
        className="w-12 h-12 rounded-lg border-2 border-gray-200 shadow-sm"
        style={{ backgroundColor: color }}
      />
      <div>
        <div className="font-medium text-gray-900">{name}</div>
        <div className="text-sm text-gray-500 font-mono">{color}</div>
        {shade && <div className="text-xs text-gray-400">{shade}</div>}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Paleta de Cores Dinâmica</h1>
        <p className="text-gray-600">
          As cores abaixo são aplicadas dinamicamente baseadas nas configurações do sistema.
        </p>
      </div>

      {/* Cores Primárias */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cores Primárias</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(primaryShades).map(([shade, color]) => (
            <ColorSwatch 
              key={shade} 
              color={color} 
              name={`Primária ${shade}`}
              shade={shade}
            />
          ))}
        </div>
      </div>

      {/* Cores Secundárias */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cores Secundárias</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(secondaryShades).map(([shade, color]) => (
            <ColorSwatch 
              key={shade} 
              color={color} 
              name={`Secundária ${shade}`}
              shade={shade}
            />
          ))}
        </div>
      </div>

      {/* Demonstração de Uso */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Demonstração de Uso</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Botões */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Botões</h3>
            <div className="space-y-2">
              <button className="btn-primary px-4 py-2 rounded-lg">
                Botão Primário
              </button>
              <button className="btn-secondary px-4 py-2 rounded-lg">
                Botão Secundário
              </button>
              <button className="btn-outline px-4 py-2 rounded-lg">
                Botão Outline
              </button>
            </div>
          </div>

          {/* Textos */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Textos</h3>
            <div className="space-y-2">
              <p className="text-primary-600 font-semibold">Texto Primário</p>
              <p className="text-secondary-600 font-semibold">Texto Secundário</p>
              <p className="text-primary-500">Texto Primário 500</p>
              <p className="text-secondary-500">Texto Secundário 500</p>
            </div>
          </div>

          {/* Backgrounds */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Backgrounds</h3>
            <div className="space-y-2">
              <div className="bg-primary-100 p-3 rounded-lg">
                <span className="text-primary-800">Background Primário 100</span>
              </div>
              <div className="bg-secondary-100 p-3 rounded-lg">
                <span className="text-secondary-800">Background Secundário 100</span>
              </div>
              <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-3 rounded-lg">
                <span className="text-white">Gradiente Primário → Secundário</span>
              </div>
            </div>
          </div>

          {/* Bordas */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Bordas</h3>
            <div className="space-y-2">
              <div className="border-2 border-primary-500 p-3 rounded-lg">
                <span className="text-gray-700">Borda Primária</span>
              </div>
              <div className="border-2 border-secondary-500 p-3 rounded-lg">
                <span className="text-gray-700">Borda Secundária</span>
              </div>
            </div>
          </div>

          {/* Estados Hover */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Estados Hover</h3>
            <div className="space-y-2">
              <button className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg transition-colors">
                Hover Primário
              </button>
              <button className="bg-secondary-500 hover:bg-secondary-600 text-white px-4 py-2 rounded-lg transition-colors">
                Hover Secundário
              </button>
            </div>
          </div>

          {/* Animações */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Animações</h3>
            <div className="space-y-2">
              <div className="animate-dynamic-glow text-2xl font-bold text-primary-600">
                Glow Dinâmico
              </div>
              <div className="animate-pulse bg-primary-200 p-3 rounded-lg">
                <span className="text-primary-800">Pulse Primário</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Código CSS */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">CSS Dinâmico Aplicado</h2>
        <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
          <pre className="text-sm">
{`:root {
  --primary-color: ${primary};
  --secondary-color: ${secondary};
  --primary-50: ${primaryShades[50]};
  --primary-100: ${primaryShades[100]};
  --primary-200: ${primaryShades[200]};
  --primary-300: ${primaryShades[300]};
  --primary-400: ${primaryShades[400]};
  --primary-500: ${primaryShades[500]};
  --primary-600: ${primaryShades[600]};
  --primary-700: ${primaryShades[700]};
  --primary-800: ${primaryShades[800]};
  --primary-900: ${primaryShades[900]};
  
  --secondary-50: ${secondaryShades[50]};
  --secondary-100: ${secondaryShades[100]};
  --secondary-200: ${secondaryShades[200]};
  --secondary-300: ${secondaryShades[300]};
  --secondary-400: ${secondaryShades[400]};
  --secondary-500: ${secondaryShades[500]};
  --secondary-600: ${secondaryShades[600]};
  --secondary-700: ${secondaryShades[700]};
  --secondary-800: ${secondaryShades[800]};
  --secondary-900: ${secondaryShades[900]};
}`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default ThemeColorDemo;
